import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { SiteShipmentSummaryData, SiteShipmentContainerData, SummaryByProtocol } from './shipment-summary-info';
import { ActivatedRoute } from '@angular/router';
import { SiteShippingToolStorage } from './../service/site-shipping-tool-storage';
import { Site } from '../../sites';
import { UrlBuilder } from '../../core';
import { GridDetails, GridHeader } from '../../sample-tracking/tracking-overview/interfaces/grid';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { ActivityLogger } from '../../activities/activity-logger';
import { ShipmentFormService } from '../shipment-form/service/shipment-form-service';

@Component({
    selector: 'shipment-summary',
    template: require('./shipment-summary.component.html'),
    styles: [require('./shipment-summary.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class ShipmentSummaryComponent {

    public data: Observable<SiteShipmentSummaryData[]>;
    public summaryData: SiteShipmentSummaryData[];
    public info: SiteShipmentSummaryData;
    public summaryByProtocolData: SummaryByProtocol[] = [];
    public toSubAddressData = '';
    public fromSubAddressData = '';
    public currentSite: Site;
    public gridDetails: GridDetails;
    public loadingComplete = false;
    public printedOn: string;
    public numberOfContainers: number;
    public showSuccess = false;
    public showSubmitAlert = false;

    constructor(
        private storage: SiteShippingToolStorage,
        private changeDectRef: ChangeDetectorRef,
        private urlBuilder: UrlBuilder,
        private activatedRoute: ActivatedRoute,
        private shipmentFormService: ShipmentFormService,
        activityLogger: ActivityLogger) {

        this.activatedRoute.params.subscribe(params => {
            this.data = this.storage.getShipmentSummaryDetails(params.id);
            this.showSuccess = params.type === 'view' ? false : true;
            this.showSubmitAlert = params.type === 'view' ? false : true;
        });
        this.printedOn = moment().local().format('DD-MMM-YYYY hh:mm A');
        this.data.subscribe((details: SiteShipmentSummaryData[]) => {
            if (!details || details.length === 0) {
                this.urlBuilder.esa().sampleManagement().siteShipping().submittedShipmentList().navigate();
            }
            this.summaryData = details;
            if (details && details.length > 0) {
                this.info = details[0];
                this.numberOfContainers = details[0].containerData ? details[0].containerData.length : 0;
                this.toSubAddressData = this.getSubAddressData([this.info.toState, this.info.toPostalCode]);
                this.fromSubAddressData = this.getSubAddressData([this.info.fromState, this.info.fromPostalCode]);
                this.loadData();
            }
            this.loadingComplete = true;
            this.changeDectRef.markForCheck();
        });
    }

    public viewAllShipments() {
        return this.urlBuilder.esa().sampleManagement().siteShipping().submittedShipmentList().navigate();
    }

    public getSubAddressData(values: string[]) {
        let address = '';
        values.forEach(t => {
            if (address.length) {
                address = address + ', ';
            }
            if (t && t.length) {
                address = address + t;
            }
        });
        return address;

    }

    private gridData(containerData: SiteShipmentContainerData[], includeTransferReason = false) {
        this.gridDetails = {
            header: this.addHeader(includeTransferReason),
            data: containerData
        };
    }

    private loadData() {
        if (!this.summaryData[0].containerData) {
            return this.gridData([]);
        }
        let includeTransferReason = false;
        this.summaryData[0].containerData.forEach(data => {
            data.children = [];
            if (data.accessionNumber && data.containerNumber) {
                data.containerBarcode = data.accessionNumber + '-' + this.shipmentFormService.pad(data.containerNumber);
            }
            if (!data.containerBarcode) {
                data.containerBarcode = this.formatBarcode(data.containerBarcodeRaw);
            }
            if (data.containerBarcode.length < 11) {
                data.containerBarcode = data.containerBarcode.replace('-', '');
            }
            if (!data.hasPermission) {
                data.protocolCode = 'No Protocol Access';
                data.cssClass = 'disable-row';
                data.visitDescription = 'No Protocol Access - If you need access, please contact your study coordinator';
            }
            if (!includeTransferReason) {
                includeTransferReason = data.transferredTo ? true : false;
            }
            this.addContainerDataToProtocol(data);
        });
        this.gridData(this.summaryData[0].containerData, includeTransferReason);
    }

    private addHeader(includeTransferReason = false) {
        let header: GridHeader[] =
            [
                {
                    displayText: 'Sample',
                    key: 'containerBarcode',
                    subFields: [{ key: 'containerLabel1' }, { key: 'containerLabel2'}],
                    sort: null
                },
                {
                    displayText: 'Barcode',
                    key: 'containerBarcodeRaw',
                    sort: null
                },
                {
                    displayText: 'Expected Return Type',
                    key: 'expectedReturnType',
                    sort: null
                },
                {
                    displayText: 'Expected Return Condition',
                    key: 'expectedReturnCondition',
                    sort: null
                },
                {
                    displayText: 'Protocol',
                    key: 'protocolCode',
                    sort: null
                },
                {
                    displayText: 'Visit Description',
                    key: 'visitDescription',
                    sort: null
                },
            ];
        let transferHeaders: GridHeader[] = [
            {
                displayText: 'Container Transferred To',
                key: 'transferredTo',
                sort: null
            },
            {
                displayText: 'Container Transferred By',
                key: 'transferredBy',
                sort: null
            },
            {
                displayText: 'Reason for Change',
                key: 'transferReason',
                sort: null
            }
        ];
        if (includeTransferReason) {
            header = header.concat(transferHeaders);
        }
        return header;
    }

    private formatBarcode(barcode: string) {
        let result = this.shipmentFormService.formatBarcode(barcode);
        if (!result) { return barcode; }
        if (!result.containerNumber) {
            return result.userEnteredBarcode;
        }
        return result.accessionNumber + '-' + this.shipmentFormService.pad(result.containerNumber);
    }

    private addContainerDataToProtocol(containerData: SiteShipmentContainerData) {
        let protocol = containerData.protocolCode;
        let data = this.summaryByProtocolData.filter(t => t.protocol === protocol);
        if (!data || data.length === 0) {
            let summaryData: SummaryByProtocol = {
                protocol: protocol,
                containerData: [],
                gridDetails: {
                    header: this.addHeader(containerData.transferredTo ? true : false),
                    data: []
                }
            };
            summaryData.containerData.push(containerData);
            summaryData.gridDetails.data.unshift(containerData);
            this.summaryByProtocolData.push(summaryData);
            return;
        }
        this.summaryByProtocolData.forEach(t => {
            if (t.protocol === protocol) {
                t.containerData.push(containerData);
                t.gridDetails.header = this.addHeader(containerData.transferredTo ? true : false);
                t.gridDetails.data.unshift(containerData);
            }
        });
    }
}